import { Children, cloneElement, forwardRef, isValidElement } from 'react';
import type { InputHTMLAttributes, ReactElement, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react';
import { cx } from './cx';

const CONTROL_CLASS = 'min-h-tap w-full rounded-sm border border-sage-300 bg-white px-4 text-body focus:border-teal-500';

export type InputProps = InputHTMLAttributes<HTMLInputElement>;

export const Input = forwardRef<HTMLInputElement, InputProps>(({ className = '', ...props }, ref) => (
  <input ref={ref} className={cx(CONTROL_CLASS, className)} {...props} />
));
Input.displayName = 'Input';

export type SelectProps = SelectHTMLAttributes<HTMLSelectElement>;

export const Select = forwardRef<HTMLSelectElement, SelectProps>(({ className = '', ...props }, ref) => (
  <select ref={ref} className={cx(CONTROL_CLASS, className)} {...props} />
));
Select.displayName = 'Select';

export type TextAreaProps = TextareaHTMLAttributes<HTMLTextAreaElement>;

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  ({ className = '', maxLength, value, ...props }, ref) => {
    const length = typeof value === 'string' ? value.length : 0;
    return (
      <div>
        <textarea
          ref={ref}
          maxLength={maxLength}
          value={value}
          className={cx(CONTROL_CLASS, 'min-h-[96px] py-3', className)}
          {...props}
        />
        {maxLength !== undefined && (
          <span className="mt-1 block text-right text-caption text-ink-muted">
            {length} / {maxLength}
          </span>
        )}
      </div>
    );
  },
);
TextArea.displayName = 'TextArea';

type FieldChildProps = { id?: string; 'aria-describedby'?: string; 'aria-invalid'?: boolean };

export interface FieldProps {
  id: string;
  label: string;
  hint?: string;
  error?: string;
  children: ReactElement<FieldChildProps>;
  className?: string;
}

/** Wraps a single form control, cloning in `id`/`aria-describedby`/`aria-invalid`. */
export const Field = ({ id, label, hint, error, children, className = '' }: FieldProps) => {
  const showHint = !!hint && !error;
  const hintId = showHint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;

  const onlyChild = Children.only(children) as ReactNode;
  const cloned = isValidElement<FieldChildProps>(onlyChild)
    ? cloneElement(onlyChild, { id, 'aria-describedby': describedBy, 'aria-invalid': !!error || undefined })
    : onlyChild;

  return (
    <div className={cx('block', className)}>
      <label htmlFor={id} className="mb-1.5 block text-label text-ink">
        {label}
      </label>
      {cloned}
      {showHint && (
        <p id={hintId} className="mt-1 text-caption text-ink-muted">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} role="alert" className="mt-1 text-caption text-alert-ink">
          {error}
        </p>
      )}
    </div>
  );
};
